import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { T } from "./adminTheme";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SearchIcon from "@mui/icons-material/Search";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

import {
  SectionCard,
  StyledTableRow,
  TabHeader,
  BoolChip,
  AvailabilityChip,
  FilterableSortableTableHead,
  useSortableTable,
  applyColumnFilters,
  RowActionButton,
} from "./shared";

import {
  checkEmployeeGlobalStatus,
  releaseEmployeeLockSafely,
} from "./recoveryHelpers";

// ✅ Optimized dialogs - prevents typing delay
import EmployeeCreateDialog from "./EmployeeCreateDialog";
import EmployeeEditDialog from "./EmployeeEditDialog";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLUMNS = [
  { id: "EmpID",             label: "Emp ID",      minWidth: 100 },
  { id: "Name",              label: "Name",        minWidth: 160 },
  { id: "MachineGroup",      label: "Group",       minWidth: 100 },
  { id: "Designation",       label: "Designation", minWidth: 120 },
  { id: "ActiveStatus",      label: "Status",      minWidth: 120 },
  { id: "avaliability",      label: "Availability",minWidth: 130 },
  { id: "activeMachineCode", label: "Machine",     minWidth: 130 },
  { id: "hasUserAccount",    label: "User Acc.",   minWidth: 130 },
  { id: "_actions",          label: "Actions", sortable: false, align: "right", minWidth: 60 },
];

function getEmployeeRecommendationMessage(status) {
  if (!status) return "Unable to analyze employee recovery status.";

  if (status.recommendation === "USE_SESSION_RECOVERY_MARK_OUT_FROM_ROSTER") {
    const rm = status.rosterMembership;
    return rm
      ? `This employee is a roster member (${rm.role}) on an active shift run by operator ${rm.hostEmployeeId} on machine ${rm.hostMachineCode}. Use Session Recovery → Mark Out from Roster.`
      : "This employee is a roster member on an active shift. Use Session Recovery → Mark Out from Roster.";
  }

  if (status.recommendation === "USE_MAN_HOUR_RECOVERY") {
    return "This employee has active man-hour records. Please use Man-hour Recovery.";
  }

  if (status.recommendation === "USE_MACHINE_HOUR_RECOVERY") {
    return "This employee has active machine-hour records. Please use Session Recovery.";
  }

  if (status.recommendation === "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") {
    return "This employee has active records in both machine-hour and man-hour sections. Please check both recovery sections.";
  }

  if (status.recommendation === "DIRECT_RELEASE_ALLOWED") {
    return "No active machine-hour or man-hour records found. Direct employee lock release is allowed.";
  }

  return "No active machine-hour or man-hour records found.";
}

export default function EmployeesTab({
  onSnack,
  goToUsers,
  goToSessionRecovery,
  goToManhourRecovery,
  adminUsername,
  externalSearch,
  setExternalSearch,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // ✅ Removed form state - now in EmployeeCreateDialog (prevents typing delay)
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const search = externalSearch ?? "";
  const setSearch = setExternalSearch ?? (() => {});
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);  // ✅ Changed from editForm to editEmployee

  const [releaseDlg, setReleaseDlg] = useState({
    open: false,
    employeeId: null,
    intent: null,
    loading: false,
    status: null,
    actionLoading: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/admin/employees-with-user-link`);
      setList(r.data || []);
      setColumnFilters({});
    } catch {
      onSnack("Failed to load employees.", "error");
    } finally {
      setLoading(false);
    }
  }, [onSnack]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();

    return list.filter((e) => (
      String(e.EmpID).toLowerCase().includes(q) ||
      e.Name?.toLowerCase().includes(q) ||
      e.MachineGroup?.toLowerCase().includes(q) ||
      e.Designation?.toLowerCase().includes(q) ||
      String(e.LinkedUserID || "").toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const { sortedRows: rows, sortField, sortDir, handleSort } =
    useSortableTable(columnFilteredRows, "EmpID");

  // ✅ Updated to accept form data from dialog (prevents re-renders)
  const save = async (formData) => {
    if (!formData.EmpID || !formData.Name || !formData.MachineGroup) {
      onSnack("Please fill all required employee fields.", "warning");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/admin/createEmployee`, formData);
      onSnack("Employee created successfully.");
      setOpenCreate(false);
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to create employee.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateEmployeeStatus = async (empId, ActiveStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/employees/${empId}/status`, {
        ActiveStatus,
      });
      onSnack("Employee status updated.");
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update status.", "error");
    }
  };

  const updateEmployeeAvailability = async (
    empId,
    avaliability,
    clearMachineCode = false
  ) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/employees/${empId}/availability`, {
        avaliability,
        clearMachineCode,
      });
      onSnack("Employee availability updated.");
      load();
    } catch (err) {
      onSnack(
        err?.response?.data?.message || "Failed to update availability.",
        "error"
      );
    }
  };

  // ✅ Updated to accept form data from dialog (prevents re-renders)
  const saveEdit = async (formData) => {
    if (!formData) return;

    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/admin/employees/${formData.EmpID}`, {
        Name: formData.Name,
        MachineGroup: formData.MachineGroup,
        Designation: formData.Designation,
        ActiveStatus: formData.ActiveStatus,
      });
      onSnack("Employee updated successfully.");
      setOpenEdit(false);
      setEditEmployee(null);
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update employee.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleViewUser = (employeeId) => {
    if (typeof goToUsers === "function") {
      goToUsers(employeeId);
      return;
    }
    onSnack(`Go to User Accounts tab and search Employee ID ${employeeId}.`, "info");
  };

  const openReleaseDialog = async (employeeId, intent) => {
    setReleaseDlg({
      open: true,
      employeeId,
      intent,
      loading: true,
      status: null,
      actionLoading: false,
    });

    try {
      const status = await checkEmployeeGlobalStatus(employeeId);

      setReleaseDlg({
        open: true,
        employeeId,
        intent,
        loading: false,
        status,
        actionLoading: false,
      });
    } catch (err) {
      setReleaseDlg({
        open: false,
        employeeId: null,
        intent: null,
        loading: false,
        status: null,
        actionLoading: false,
      });

      onSnack(
        err?.response?.data?.error || "Failed to analyze employee global status.",
        "error"
      );
    }
  };

  const closeReleaseDialog = () => {
    if (releaseDlg.actionLoading) return;

    setReleaseDlg({
      open: false,
      employeeId: null,
      intent: null,
      loading: false,
      status: null,
      actionLoading: false,
    });
  };

  const confirmSafeRelease = async () => {
    const { employeeId, status } = releaseDlg;

    if (!status?.canDirectRelease) {
      onSnack("Direct release is blocked. Use the recommended recovery section.", "warning");
      return;
    }

    setReleaseDlg((s) => ({ ...s, actionLoading: true }));

    try {
      await releaseEmployeeLockSafely({
        empId: employeeId,
        cleanedBy: adminUsername || "admin",
        reason: "Manual employee release from Admin Employees tab after global recovery check",
      });

      onSnack("Employee lock released successfully.", "success");
      closeReleaseDialog();
      load();
    } catch (err) {
      onSnack(
        err?.response?.data?.error || "Employee release failed.",
        "error"
      );
      setReleaseDlg((s) => ({ ...s, actionLoading: false }));
    }
  };

  const goToMachineRecovery = () => {
    const employeeId = releaseDlg.employeeId;
    closeReleaseDialog();

    if (typeof goToSessionRecovery === "function") {
      goToSessionRecovery({ employeeId });
      return;
    }

    onSnack(`Open Session Recovery and search Employee ID ${employeeId}.`, "info");
  };

  const goToManhourRecoverySection = () => {
    const employeeId = releaseDlg.employeeId;
    const status = releaseDlg.status;
    closeReleaseDialog();

    const firstOpenShift = status?.manHour?.openShifts?.[0];

    if (typeof goToManhourRecovery === "function") {
      goToManhourRecovery({
        employeeId,
        supervisorId: firstOpenShift?.supervisor_id,
        stationCode: firstOpenShift?.station_code,
      });
      return;
    }

    onSnack(
      `Open Man-hour Recovery and search Employee ID ${employeeId}.`,
      "info"
    );
  };

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="Employees"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            onAdd={() => {
              setOpenCreate(true);  // ✅ Dialog handles its own form reset
            }}
            btnLabel="Create Employee"
            btnColor="linear-gradient(90deg,#10b981,#059669)"
          />
          <Divider />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS.length}
                        sx={{ textAlign: "center", py: 5, color: t.muted }}
                      >
                        {search ? "No employees match your search." : "No employees found."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((e) => {
                      const hasUserAccount = Number(e.hasUserAccount) === 1;
                      const isActive = Number(e.ActiveStatus) === 1;
                      const isAvailable = Number(e.avaliability) === 1;

                      return (
                        <StyledTableRow key={e.EmpID}>
                          <TableCell sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                            {e.EmpID}
                          </TableCell>
                          <TableCell>{e.Name}</TableCell>
                          <TableCell>{e.MachineGroup}</TableCell>
                          <TableCell>{e.Designation}</TableCell>

                          {/* Status — inline activate/deactivate */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <BoolChip value={e.ActiveStatus} />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title={isActive ? "Deactivate employee" : "Activate employee"}
                                  icon={<PowerSettingsNewIcon />}
                                  color={isActive ? "error" : "success"}
                                  onClick={() => updateEmployeeStatus(e.EmpID, isActive ? 0 : 1)}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Availability — inline toggle / release */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <AvailabilityChip value={e.avaliability} />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title={isAvailable ? "Mark as In Use" : "Set as Available — global session check"}
                                  icon={<SwapHorizIcon />}
                                  color={isAvailable ? "warning" : "success"}
                                  onClick={() => {
                                    if (isAvailable) {
                                      updateEmployeeAvailability(e.EmpID, 0, false);
                                    } else {
                                      openReleaseDialog(e.EmpID, "available");
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Machine — inline reset active machine */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Typography sx={{
                                fontSize: 12,
                                fontFamily: "monospace",
                                fontWeight: e.activeMachineCode ? 600 : 400,
                                color: e.activeMachineCode ? t.primary : t.muted,
                              }}>
                                {e.activeMachineCode || "—"}
                              </Typography>
                              {e.activeMachineCode && (
                                <Box sx={{
                                  opacity: 0,
                                  ".MuiTableRow-root:hover &": { opacity: 1 },
                                  transition: "opacity 0.15s ease",
                                }}>
                                  <RowActionButton
                                    title="Reset active machine — global session check"
                                    icon={<LinkOffIcon />}
                                    color="warning"
                                    onClick={() => openReleaseDialog(e.EmpID, "reset_machine")}
                                  />
                                </Box>
                              )}
                            </Box>
                          </TableCell>

                          {/* User Acc. — inline go to users tab */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip
                                size="small"
                                label={hasUserAccount ? "Linked" : "Missing"}
                                color={hasUserAccount ? "success" : "warning"}
                              />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title="View in User Accounts tab"
                                  icon={<SearchIcon />}
                                  color="primary"
                                  onClick={() => handleViewUser(e.EmpID)}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Actions — edit only */}
                          <TableCell align="right">
                            <RowActionButton
                              title="Edit employee"
                              icon={<EditIcon />}
                              color="primary"
                              onClick={() => {
                                setEditEmployee({ ...e });  // ✅ Changed from setEditForm
                                setOpenEdit(true);
                              }}
                            />
                          </TableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        {/* ✅ Optimized create dialog - no typing delay */}
        <EmployeeCreateDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSave={save}
          saving={saving}
        />

        {/* ✅ Optimized edit dialog - no typing delay */}
        <EmployeeEditDialog
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          onSave={saveEdit}
          saving={saving}
          employee={editEmployee}
        />

        <Dialog
          open={releaseDlg.open}
          onClose={closeReleaseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              {releaseDlg.status?.canDirectRelease ? (
                <CheckCircleIcon color="success" />
              ) : (
                <WarningAmberIcon color="warning" />
              )}
              <Typography variant="h6" fontWeight={700}>
                Employee Global Recovery Check
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent>
            {releaseDlg.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                <Alert
                  severity={releaseDlg.status?.canDirectRelease ? "success" : "warning"}
                >
                  {getEmployeeRecommendationMessage(releaseDlg.status)}
                </Alert>

                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Employee ID
                  </Typography>
                  <Typography variant="body2">{releaseDlg.employeeId}</Typography>
                </Box>

                {releaseDlg.status && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${t.border}`,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Machine-hour records
                        </Typography>
                        <Typography variant="body2">
                          Open shifts:{" "}
                          {releaseDlg.status.machineHour?.openShiftCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open operations:{" "}
                          {releaseDlg.status.machineHour?.openOperationCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open jobs:{" "}
                          {releaseDlg.status.machineHour?.openJobCount || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${t.border}`,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Man-hour records
                        </Typography>
                        <Typography variant="body2">
                          Open shifts:{" "}
                          {releaseDlg.status.manHour?.openShiftCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open details:{" "}
                          {releaseDlg.status.manHour?.openDetailCount || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {releaseDlg.status?.canDirectRelease && (
                  <Alert severity="info">
                    Confirming will release this employee lock from the common
                    employee table by setting availability to available and clearing
                    active machine code.
                  </Alert>
                )}

                {/* Roster membership detail block */}
                {releaseDlg.status?.rosterMembership && (
                  <Box sx={{
                    p: 2, borderRadius: "10px",
                    background: t.amberAlpha,
                    border: `0.5px solid ${t.amber}35`,
                    borderLeft: `2.5px solid ${t.amber}`,
                  }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: isDark ? "#fbbf24" : "#d97706", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1 }}>
                      ⚠ Roster Membership Detected
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: t.primary, mb: 1, lineHeight: 1.6 }}>
                      This employee is currently part of another operator's shift roster.
                      They do not have their own login session.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                      <Typography sx={{ fontSize: 12, color: t.primary }}>
                        • Host operator: <b>{releaseDlg.status.rosterMembership.hostEmployeeId}</b>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: t.primary }}>
                        • Machine: <b>{releaseDlg.status.rosterMembership.hostMachineCode}</b>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: t.primary }}>
                        • Role: <b>{releaseDlg.status.rosterMembership.role}</b>
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: t.secondary, mt: 1.5, lineHeight: 1.5 }}>
                      Use <b>Session Recovery → Mark Out from Roster</b> to properly log this
                      employee out. The host shift will continue running.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeReleaseDialog} disabled={releaseDlg.actionLoading}>
              Cancel
            </Button>

            {!releaseDlg.loading && !releaseDlg.status?.canDirectRelease && (
              <>
                {releaseDlg.status?.recommendation === "USE_SESSION_RECOVERY_MARK_OUT_FROM_ROSTER" && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CleaningServicesIcon />}
                    onClick={() => {
                      closeReleaseDialog();
                      const rm = releaseDlg.status?.rosterMembership;
                      if (rm?.hostMachineCode && typeof goToSessionRecovery === "function") {
                        goToSessionRecovery({ machineCode: rm.hostMachineCode });
                      } else if (typeof goToSessionRecovery === "function") {
                        goToSessionRecovery({ employeeId: releaseDlg.employeeId });
                      }
                    }}
                    sx={{ fontWeight: 600, fontSize: 13 }}
                  >
                    Open Session Recovery
                  </Button>
                )}

                {(releaseDlg.status?.recommendation === "USE_MACHINE_HOUR_RECOVERY" ||
                  releaseDlg.status?.recommendation ===
                    "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") && (
                  <Button
                    variant="outlined"
                    startIcon={<CleaningServicesIcon />}
                    onClick={goToMachineRecovery}
                  >
                    Open Session Recovery
                  </Button>
                )}

                {(releaseDlg.status?.recommendation === "USE_MAN_HOUR_RECOVERY" ||
                  releaseDlg.status?.recommendation ===
                    "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CleaningServicesIcon />}
                    onClick={goToManhourRecoverySection}
                  >
                    Open Man-hour Recovery
                  </Button>
                )}
              </>
            )}

            {!releaseDlg.loading && releaseDlg.status?.canDirectRelease && (
              <Button
                variant="contained"
                color="success"
                onClick={confirmSafeRelease}
                disabled={releaseDlg.actionLoading}
                startIcon={
                  releaseDlg.actionLoading ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                Release Employee Lock
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
}